import { createSelector } from 'reselect';
import { formatDate } from 'utils/date';
import { selectUserMassPriceSurveyConfig } from 'redux/user/selectors';
import { GLOBAL_LEVEL_NOT_APPLICABLE } from 'common/constants';

export function selectMarketPrice(state) {
  return state.marketPrice;
}

export function selectFilter(state) {
  return selectMarketPrice(state).filter;
}

export function selectActiveCurrencyPlan(state) {
  return selectMarketPrice(state).currencyPlan;
}

export function selectMarketPriceTable(state) {
  return selectMarketPrice(state).table;
}

export function selectMarketPriceDataLoading(state) {
  return selectMarketPriceTable(state).loading;
}

export function selectMarketPriceDataExporting(state) {
  return selectMarketPriceTable(state).exporting;
}

function selectPlans(state) {
  return selectMarketPriceTable(state).plans;
}

export const selectDefaultPlans = createSelector(
  selectPlans,
  plans => plans.filter(plan => plan.current).map(plan => plan.id)
);

export function selectIsFilterLoaded(state) {
  return selectPlans(state).length > 0;
}

export const selectPlanOptions = createSelector(
  selectPlans,
  (plans) => plans.map(({ id, name }) => ({
    value: id,
    label: name,
  }))
);

export function selectPayMarkets(state) {
  return selectMarketPriceTable(state).paymarkets;
}

/**
 * @returns {Array}
 * */
export function groupPaymarkets(payMarkets, { valueKey = 'name', isOptionDisabled } = {}) {
  const groups = payMarkets.reduce((paymarketGroups, paymarket) => {
    const group = paymarket['PaymarketGroup.name'] || paymarket.PaymarketGroup?.name || 'noGroup';
    return {
      ...paymarketGroups,
      [group]: paymarketGroups[group] ? [...paymarketGroups[group], paymarket] : [paymarket],
    };
  }, {});

  const groupKeys = Object.keys(groups);
  const sortedGroupKeys = groupKeys
    .filter(key => key !== 'noGroup')
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  const noGroupKey = groupKeys.find(key => key === 'noGroup');
  const orderedKeys = noGroupKey ? [...sortedGroupKeys, noGroupKey] : sortedGroupKeys;

  return orderedKeys.reduce((result, key) => {
    return key === 'noGroup' ? [
      ...result,
      ...groups[key].map((paymarket) => ({
        label: paymarket.name,
        value: paymarket[valueKey],
        disabled: isOptionDisabled ? isOptionDisabled(paymarket) : false,
      })),
    ] : [
      ...result,
      {
        label: key,
        value: `group-${key}`,
        disabled: isOptionDisabled ? groups[key].every(paymarket => isOptionDisabled(paymarket)) : false,
        options: groups[key].map((paymarket) => ({
          label: paymarket.name,
          value: paymarket[valueKey],
          disabled: isOptionDisabled ? isOptionDisabled(paymarket) : false,
        })),
      },
    ];
  }, []);
}

export const selectPayMarketOptions = createSelector(
  selectPayMarkets,
  (payMarkets) => groupPaymarkets(payMarkets),
);

export const selectNoIndexedPayMarkets = createSelector(
  selectPayMarkets,
  (paymarkets) => paymarkets.filter(paymarket => !paymarket.isIndexed)
);

export const selectNoIndexedPayMarketOptions = createSelector(
  selectNoIndexedPayMarkets,
  (payMarkets) => {
    const groups = payMarkets.reduce((paymarketGroups, paymarket) => {
      const group = paymarket['PaymarketGroup.name'] || 'noGroup';
      return {
        ...paymarketGroups,
        [group]: paymarketGroups[group] ? [...paymarketGroups[group], paymarket.name] : [paymarket.name],
      };
    }, {});

    return Object.keys(groups).reduce((result, key) => {
      return key === 'noGroup' ? [
        ...result,
        ...groups[key].map((name) => ({ label: name, value: name })),
      ] : [
        ...result,
        { label: key, value: `group-${key}`, options: groups[key].map((name) => ({ label: name, value: name })) },
      ];
    }, []);
  },
);


export function selectCompanyJobs(state) {
  return selectMarketPriceTable(state).companyJobs;
}

export function selectMarketPriceSurveys(state) {
  return selectMarketPriceTable(state).surveys;
}

export function selectJobFamilies(state) {
  return selectMarketPriceTable(state).jobFamilies;
}

export function selectJobLevels(state) {
  return selectMarketPriceTable(state).jobLevels;
}

function selectCompanyJobsFiltered(state) {
  return selectMarketPriceTable(state).companyJobsFiltered;
}

function selectJobFamiliesFiltered(state) {
  return selectMarketPriceTable(state).jobFamiliesFiltered;
}

export const selectMarketPriceSurveyOptions = createSelector(
  selectMarketPriceSurveys,
  (surveys) => surveys.map(({ name, id }) => ({
    label: name,
    value: id,
  }))
);

export const selectJobFamiliesOptions = createSelector(
  selectJobFamilies,
  (jobFamilies) => jobFamilies.map(({ id, familyTitle, subFamilies }) => ({
    value: `family-${id}`,
    label: familyTitle,
    options: subFamilies.map(subFamily => ({ value: subFamily.id, label: subFamily.title })),
  }))
);


export const selectJobLevelsOptions = createSelector(
  selectJobLevels,
  (jobLevels) => jobLevels.map(({ id, levelCode }) => ({
    value: id,
    label: levelCode,
  })),
);

function selectGrades(state) {
  return selectMarketPriceTable(state).grades;
}

export const selectGradesOptions = createSelector(
  selectGrades,
  (grades) => grades.map(({ grade }) => ({ value: grade, label: grade })),
);

export function selectEffectiveDateList(state) {
  return selectMarketPriceTable(state).effectiveDates;
}

const filterTabs = (key, tabs, valueKey, labelKey, groupByKey, filter) => {
  let filteredTabs = tabs;
  if (groupByKey && groupByKey === key && filter[key] && filter[key].length) {
    filteredTabs = tabs.filter((tab) => filter[key].includes(tab[valueKey]));
  }
  return filteredTabs.map((tab) => ({
    value: tab[valueKey],
    label: tab[labelKey],
  }));
};

export const selectTabs = createSelector(
  selectPayMarkets,
  selectPlans,
  selectJobFamiliesFiltered,
  selectCompanyJobsFiltered,
  selectFilter,
  selectGroupByKey,
  (paymarkets, plans, jobFamiliesFiltered, companyJobsFiltered, filter, groupByKey) => {
    return {
      payMarket: filterTabs('payMarket', paymarkets, 'name', 'name', groupByKey, filter),
      plan: filterTabs('plan', plans, 'id', 'name', groupByKey, filter),
      jobFamily: filterTabs('jobFamily', jobFamiliesFiltered, 'id', 'familyTitle', groupByKey, filter),
      companyJob: filterTabs('companyJob', companyJobsFiltered, 'id', 'jobTitle', groupByKey, filter),
    };
  }
);

export const selectPaymarketTabs = createSelector(
  selectNoIndexedPayMarkets,
  state => selectFilter(state).payMarket,
  (paymarkets, filteredPaymarkets) => {
    const tabs = paymarkets.map((paymarket) => ({
      value: paymarket.name,
      label: paymarket.name,
    }));

    return filteredPaymarkets.length
      ? tabs.filter((tab) => filteredPaymarkets.includes(tab.value))
      : tabs;
  }
);

export function selectGroupByKey(state) {
  return selectMarketPriceTable(state).groupByKey;
}

export const selectSelectedPaymarket = (state) => {
  return selectPayMarkets(state).find(paymarket => paymarket.name === selectGroupByValue(state));
};

export function selectAge(state) {
  return selectMarketPriceTable(state).age;
}

export function selectGroupByValue(state) {
  return selectMarketPriceTable(state).groupByValue;
}

export function selectMarketPriceSorting(state) {
  return selectMarketPrice(state).sorting;
}

export function selectAddSurvey(state) {
  return selectMarketPrice(state).addSurvey;
}

export function selectAddSurveyLoading(state) {
  return selectAddSurvey(state).loading;
}

export function selectAddSurveyData(state) {
  return selectAddSurvey(state).data;
}

export function selectAddSurveyEffectiveDates(state) {
  return selectAddSurvey(state).effectiveDates;
}

export const selectAddSurveyEffectiveDateOptions = createSelector(
  selectAddSurveyEffectiveDates,
  (effectiveDates) => effectiveDates.map((effectiveDate) => ({
    label: formatDate(effectiveDate),
    value: effectiveDate,
  })),
);

export function selectAddSurveyJobFamilies(state) {
  return selectAddSurvey(state).jobFamilies;
}

export const selectAddSurveyJobFamilyOptions = createSelector(
  selectAddSurveyJobFamilies,
  (jobFamilies) => jobFamilies.map(
    family => ({
      label: family.familyTitle,
      value: family.id,
      options: family.SurveyJobSubFamilies.map(
        (subFamily) => ({
          value: subFamily.id,
          label: subFamily.subFamilyTitle,
          options: subFamily.SurveyJobFocus.map(focus => ({ value: focus.id, label: focus.focusTitle })),
        })
      ),
    })
  ),
);

export function selectAddSurveyCareerLevels(state) {
  return selectAddSurvey(state).careerLevels;
}

export function selectAddSurveyGlobalLevels(state) {
  return selectAddSurvey(state).globalLevels;
}

export function selectAddSurveyMappedJobLevels(state) {
  return selectAddSurvey(state).mappedJobLevels;
}

export function selectAddSurveyMappedJobGlobalLevels(state) {
  return selectAddSurvey(state).mappedJobGlobalLevels;
}

export const selectMappedSurveyLevels = createSelector(
  selectAddSurvey,
  (state) => state.mappedSurveyLevels,
);

export const selectAddSurveyCareerLevelsOptions = createSelector(
  selectAddSurveyCareerLevels,
  selectAddSurveyMappedJobLevels,
  (levels, mappedLevels) => {
    const jobLevels = levels.map((level) => {
      return { value: level.id, label: `${level.levelCode} ${level.levelTitle}` }
    })

    const preMappedLevels = mappedLevels.map((level) => {
      return { value: level.id, label: `${level.levelCode} ${level.levelTitle}` }
    })

    return [
      ...(preMappedLevels.length ? [{ label: 'Pre-Mapped Levels', options: preMappedLevels, value: 'pre-mapped' }] : []),
      ...jobLevels,
    ];
  },
);

export const selectAddSurveyGlobalLevelsOptions = createSelector(
  selectAddSurveyGlobalLevels,
  selectAddSurveyMappedJobGlobalLevels,
  (levels, mappedGlobalLevels) => {

    const jobLevels = levels.map((level) => {
      return { value: level.id, label: `${level.levelCode} ${level.levelTitle}` }
    })

    const preMappedLevels = mappedGlobalLevels.map((level) => {
      return { value: level.id, label: `${level.levelCode} ${level.levelTitle}` }
    })

    return [
      { label: 'Not Applicable', value: GLOBAL_LEVEL_NOT_APPLICABLE },
      ...(preMappedLevels.length ? [{ label: 'Pre-Mapped Levels', options: preMappedLevels, value: 'pre-mapped' }] : []),
      ...jobLevels,
    ];
  },
);

export function selectAddSurveySurveys(state) {
  return selectAddSurvey(state).surveys;
}

export const selectAddSurveySurveysOptions = createSelector(
  selectAddSurveySurveys,
  (surveys) => surveys.map(({ id, name }) => ({ label: name, value: id })),
);

export function selectAddSurveyVendors(state) {
  return selectAddSurvey(state).vendors;
}

export const selectAddSurveyVendorOptions = createSelector(
  selectAddSurveyVendors,
  (vendors) => vendors.map(vendor => ({
    label: vendor.name,
    value: vendor.id,
  })),
);

const selectJobFamilyOptions = createSelector(
  selectAddSurveyJobFamilies,
  (jobFamilies) => jobFamilies.map(
    family => ({
      label: family.familyTitle,
      value: family.id,
    })
  ),
);

const selectJobSubFamilyOptions = createSelector(
  selectAddSurveyJobFamilies,
  (jobFamilies) => jobFamilies.reduce((res, family) => {
    return [...res, ...family.SurveyJobSubFamilies.map(
      (subFamily) => ({
        value: subFamily.id,
        label: subFamily.subFamilyTitle,
      })
    )];
  }, [])
);

const selectJobFocusOptions = createSelector(
  selectAddSurveyJobFamilies,
  (jobFamilies) => jobFamilies.reduce((res, family) => {
    return [...res, ...family.SurveyJobSubFamilies.reduce((subRes, subFamily) => {
      return [...subRes, ...subFamily.SurveyJobFocus.map(focus => ({ value: focus.id, label: focus.focusTitle }))];
    }, [])];
  }, [])
);

export const selectAddSurveyFilterOptions = createSelector(
  selectAddSurveyVendorOptions,
  selectAddSurveyEffectiveDateOptions,
  selectJobFamilyOptions,
  selectJobSubFamilyOptions,
  selectJobFocusOptions,
  selectAddSurveyCareerLevelsOptions,
  selectAddSurveyGlobalLevelsOptions,
  selectAddSurveySurveysOptions,
  (vendor, effectiveDate, jobFamily, jobSubFamily, jobFocus, careerLevel, globalLevel, survey) => ({
    vendor,
    effectiveDate,
    jobFamily,
    jobSubFamily,
    jobFocus,
    careerLevel,
    globalLevel,
    survey,
  })
);

export function selectAddSurveyFilter(state) {
  return selectMarketPrice(state).addSurveyFilter;
}

export function selectAddSurveySorting(state) {
  return selectMarketPrice(state).addSurveySorting;
}

export function selectMarketPriceComposites(state) {
  return selectMarketPrice(state).composites;
}

export function selectMarketPriceCompositesData(state) {
  return selectMarketPriceComposites(state).data;
}

export function selectMarketPriceCuts(state) {
  return selectMarketPrice(state).cuts;
}

export function selectMarketPriceCutsData(state) {
  return selectMarketPriceCuts(state).data;
}

export function selectMarketPriceCutsDataLoading(state) {
  return selectMarketPriceCuts(state).loading;
}

export function selectSurveyDataCutPaymarketMappings(state) {
  return selectMarketPriceCuts(state).surveyDataCutPaymarketMappings;
}

export const selectSurveyDataCutPaymarketMappingsMap = createSelector(
  selectSurveyDataCutPaymarketMappings,
  selectUserMassPriceSurveyConfig,
  selectGroupByValue,
  (mappings, userMassPriceSurveyConfig, paymarket) => {

    const surveyDataCutPaymarketMappingsMap = mappings.reduce((accum, mapping) => {
      if (!accum[mapping.surveyId]) {
        accum[mapping.surveyId] = [];
      }

      accum[mapping.surveyId].push(mapping);

      return accum;
    }, {});

    const paymarketConfig = userMassPriceSurveyConfig[paymarket] || [];

    const sortedEntries = Object.entries(surveyDataCutPaymarketMappingsMap).sort(([surveyIdA], [surveyIdB]) => {
      const positionA = paymarketConfig.indexOf(surveyIdA);
      const positionB = paymarketConfig.indexOf(surveyIdB);
      if (positionA === -1) {
        return 1;
      }
      if (positionB === -1) {
        return -1;
      }
      return positionA > positionB ? 1 : -1;
    })

    return sortedEntries.reduce((res, [surveyId, mappings]) => {
      return {
        ...res,
        [surveyId]: mappings
      }
    }, {})
  }
)

export function selectSurveyData(state) {
  return selectMarketPriceCuts(state).surveyData;
}

export const selectSurveyOptions = createSelector(
  selectSurveyDataCutPaymarketMappingsMap,
  (mappingsMap) => {
    return Object.entries(mappingsMap).map(([surveyId, mappings]) => {
      return { value: surveyId, label: mappings[0]?.surveyBlob?.name || surveyId}
    })
  }
)

export function selectMarketPriceCutsJobMode(state) {
  return selectMarketPriceCuts(state).jobMode;
}
